var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect } from 'react';
import MenuItemContext from './private/utils/MenuItemContext';
import useMenuItemContext from './private/utils/useMenuItemContext';
import mergeClassnames from '../mergeClassnames/mergeClassnames';
import GenericCheckAlternative from '../private/icons/GenericCheckAlternative';
import useRegisterChild from '../private/utils/useRegisterChild';
const MenuItemRoot = React.forwardRef((_a, ref) => {
    var _b;
    var { as, children, width, isSelected, isActive, className } = _a, rest = __rest(_a, ["as", "children", "width", "isSelected", "isActive", "className"]);
    const Component = as || 'button';
    const states = {
        selected: isSelected,
        active: isActive,
    };
    const { state, registerChild } = useRegisterChild();
    const isNoBg = (_b = state.childrens) === null || _b === void 0 ? void 0 : _b.find((name) => name === 'Radio' || name === 'Checkbox');
    const innerSelected = isNoBg ? false : isSelected;
    return (React.createElement(MenuItemContext.Provider, { value: Object.assign(Object.assign(Object.assign({}, states), state), { registerChild }) },
        React.createElement(Component, Object.assign({ ref: ref, className: mergeClassnames('flex gap-2 justify-between items-center p-2 bg-transparent rounded-moon-i-sm', 'text-moon-14 text-bulma focus:outline-none focus:shadow-focus cursor-pointer', 'hover:bg-heles transition', width ? width : 'w-full', (innerSelected || isActive) && 'bg-heles', className && className) }, ((!as || as === 'button') && { type: 'button' }), rest), children)));
});
const Title = ({ children }) => {
    const { registerChild } = useMenuItemContext('MenuItem.Title');
    useEffect(() => {
        registerChild && registerChild('Title');
    }, []);
    return (React.createElement("span", { className: "block grow text-start text-bulma overflow-hidden" }, children));
};
const MultiTitle = ({ title, text }) => {
    const { registerChild } = useMenuItemContext('MenuItem.MultiTitle');
    useEffect(() => {
        registerChild && registerChild('MultiTitle');
    }, []);
    return (React.createElement("span", { className: "block grow text-start text-bulma overflow-hidden" },
        React.createElement("span", { className: "block text-bulma text-moon-14" }, title),
        React.createElement("span", { className: "flex text-trunks text-moon-12" },
            React.createElement("span", { className: "flex-1 truncate" }, text))));
};
const Radio = ({ isSelected, className, ['aria-label']: ariaLabel, }) => {
    const { selected = isSelected, registerChild } = useMenuItemContext('MenuItem.Items');
    useEffect(() => {
        registerChild && registerChild('Radio');
    }, []);
    const ariaLabelValue = ariaLabel ? ariaLabel : 'Radio option';
    return (React.createElement("span", { className: "flex w-6 h-6 justify-center items-center" },
        React.createElement("span", { role: "radio", "aria-checked": selected, "aria-label": ariaLabelValue, className: mergeClassnames('block relative w-4 h-4 rounded-full shadow-[0_0_0_1px_inset] shadow-trunks', 'moon-checked:shadow-piccolo after:h-2 after:w-2 after:rounded-full after:absolute', 'after:top-1/2 after:left-1/2 after:-translate-y-1/2 after:-translate-x-1/2', 'after:bg-piccolo after:transition-transform after:scale-0', 'moon-checked:after:scale-100', className) })));
};
const Checkbox = ({ isSelected, className, ['aria-label']: ariaLabel, }) => {
    const { selected = isSelected, registerChild } = useMenuItemContext('MenuItem.Checkbox');
    useEffect(() => {
        registerChild && registerChild('Checkbox');
    }, []);
    const ariaLabelValue = ariaLabel ? ariaLabel : 'Checkbox';
    return (React.createElement("span", { className: "flex w-6 h-6 justify-center items-center relative" },
        React.createElement("span", { role: "checkbox", "aria-checked": selected, "aria-label": ariaLabelValue, className: mergeClassnames('absolute top-1 start-1 flex w-4 h-4 items-center justify-center', 'shadow-[0_0_0_1px_inset] transition-colors text-moon-16 rounded-moon-s-xs shadow-trunks', 'text-goten moon-checked:shadow-none moon-checked:bg-piccolo', className) },
            React.createElement(GenericCheckAlternative, { className: mergeClassnames('transition-opacity', selected ? 'opacity-100' : 'opacity-0') }))));
};
const MenuItem = Object.assign(MenuItemRoot, {
    Title,
    MultiTitle,
    Checkbox,
    Radio,
});
export default MenuItem;
